import React, { useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  TextField,
  InputAdornment,
  Avatar,
  Typography,
  useMediaQuery,
  Popover,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Logout } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import { dashboardShell } from "../theme/dashboardShell";
import { useLogoutMutation } from "../redux/api/authApi";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import { signOut } from "../redux/Reducers/authSlice";
import {
  formatRoleLabel,
  getUserAvatarInitials,
  getUserDisplayName,
} from "../utils/userDisplay";
import NotificationBell from "./NotificationBell";

export default function Topbar() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth?.user);
  const userRole = user?.role;
  const displayName = getUserDisplayName(user);
  const roleLabel = formatRoleLabel(userRole);
  const initials = getUserAvatarInitials(user);
  const [logout] = useLogoutMutation();

  const handleSignOut = async () => {
    handleClose();
    try {
      await logout({}).unwrap();
    } catch (e) {
      // Best-effort: even if server logout fails, we still clear local auth.
    } finally {
      dispatch(signOut());
      navigate("/", { replace: true });
    }
  };

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: 0,
        zIndex: (t) => t.zIndex.drawer - 1,
        bgcolor: dashboardShell.surface.paper,
        borderBottom: dashboardShell.header.border,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          gap: 2,
          py: 1,
          minHeight: 64,
          px: { xs: 2, sm: 3 },
        }}
      >
        <TextField
          fullWidth={isSm}
          placeholder="Search users, assets, analytics..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
            // endAdornment: isSm ? (
            //   <InputAdornment position="end">
            //     <Chip
            //       size="small"
            //       label="⌘K"
            //       sx={{
            //         bgcolor: dashboardShell.surface.search,
            //         border: "1px solid",
            //         borderColor: "divider",
            //         height: 26,
            //         fontWeight: 700,
            //         fontSize: 11,
            //       }}
            //     />
            //   </InputAdornment>
            // ) : null,
            sx: {
              borderRadius: 2,
              bgcolor: dashboardShell.surface.search,
              maxWidth: { sm: 420, md: 480 },
              "& fieldset": { border: "none" },
            },
          }}
          size="small"
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <NotificationBell />
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: "pointer", userSelect: "none" }}
            ref={anchorRef}
            onClick={() => setOpen(true)}
          >
            <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>{initials}</Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography fontWeight={700} lineHeight={1.15} fontSize={14}>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {roleLabel || "—"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Toolbar>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,.12)",
            mt: 1,
            minWidth: 240,
            p: 2,
          },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Avatar sx={{ width: 48, height: 48, fontSize: 18 }}>{initials}</Avatar>
          <Box>
            <Typography fontWeight={600}>{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email || "—"}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <Stack spacing={0.5}>
          {/* <Button
            startIcon={<Person />}
            fullWidth
            sx={{ justifyContent: "flex-start", textTransform: "none", px: 1 }}
            onClick={() => {
              handleClose();
              navigate("/asset/settings");
            }}
          >
            View Profile
          </Button>
          <Button
            startIcon={<Settings />}
            fullWidth
            sx={{ justifyContent: "flex-start", textTransform: "none", px: 1 }}
            onClick={() => {
              handleClose();
              navigate("/asset/settings");
            }}
          >
            Settings
          </Button> */}
          <Button
            startIcon={<Logout />}
            fullWidth
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              px: 1,
              color: "error.main",
            }}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </Stack>
      </Popover>
    </AppBar>
  );
}
